/* Link requests + tenant search API helpers. */

import { api } from "./api";

export type TenantSearchResult = {
  slug: string;
  display_name: string;
  legal_name: string;
  company_type: string;
  country: string;
  registration_number: string;
};

export type LinkRequest = {
  id: number;
  initiator_slug: string;
  initiator_name: string;
  initiator_type: string;
  target_slug: string | null;
  target_name: string | null;
  target_country: string;
  target_registration_number: string;
  target_email: string;
  contact_name: string;
  /** The person who sent it — shown on the Inbox card so there is someone
   *  to call back. Empty on very old requests with no owner left. */
  sender_email?: string;
  sender_name?: string;
  sender_phone?: string;
  kind: string;
  /** Which side sells: true = the initiator sells to the target. Null on
   *  requests sent before the field existed. */
  initiator_is_seller: boolean | null;
  /** "Оба" on the unified form: the partner is client and supplier at once.
   *  One accept creates the link in both directions. */
  both_directions: boolean;
  status: string;
  message: string;
  created_at: string;
  responded_at: string | null;
  /** Last time the invitation was pushed out again from the Sent tab. */
  resent_at: string | null;
  /** Last correction the sender made. A request is edited, never re-sent, so
   *  this — not `created_at` — is what changed about it. */
  edited_at: string | null;
  /** When the recipient cleared it from their inbox, and what they said. */
  removed_at: string | null;
  removal_comment: string;
  /** Whether we may ask again: only 24h after a removal. */
  can_resend: boolean;
  /** Whether the other company's public page opens for us; false greys the
   *  profile icon out rather than open «Компания не найдена». */
  party_page_open?: boolean;
  expires_at: string;
  supplier_link_id?: number | null;
};

export type LinkVisibility = {
  supplier_link_id: number;
  select_all_categories: boolean;
  visible_category_ids: number[];
  visible_product_ids: number[];
  /** Дистрибьюция vs эксклюзивная дистрибьюция — the seller's grant for this
   *  buyer, set where category access is opened. */
  is_exclusive: boolean;
  /** Exclusivity granted per single category (standard mode only). */
  exclusive_category_ids?: number[];
};

/** A confirmed partner pin on "Мои связи" — only partners whose row has the
 *  show-on-map checkbox on, with coordinates from their own profile. */
export type PartnerMapPoint = {
  slug: string;
  name: string;
  company_type: string;
  country: string;
  /** Null when the partner cannot be plotted — see unmappable_reason. */
  lat: number | null;
  lng: number | null;
  /** Which end(s) of the trade they are for us. */
  directions: ("client" | "supplier")[];
  is_exclusive: boolean;
  /** "" = plotted; "hidden" (show-on-map off) | "no_coordinates". Lets the
   *  map explain why its count differs from My Partners (OE-A12). */
  unmappable_reason?: "" | "hidden" | "no_coordinates";
};

/** English fallbacks for relationship kinds. Translated copies live under the
 *  `link.kind.*` i18n keys — see {@link KIND_I18N}. */
export const KIND_LABELS: Record<string, string> = {
  "mfg→wholesaler": "Manufacturer → Wholesaler",
  "wholesaler→mfg": "Wholesaler → Manufacturer",
  "mfg→shop": "Manufacturer → Shop",
  "shop→mfg": "Shop → Manufacturer",
  "wholesaler→shop": "Wholesaler → Shop",
  "shop→wholesaler": "Shop → Wholesaler",
  "mfg→mfg": "Manufacturer → Manufacturer",
  "wholesaler→wholesaler": "Wholesaler → Wholesaler",
  "shop→shop": "Shop → Shop",
};

/** Maps a relationship kind to its i18n key so the compose modal can render the
 *  dropdown options in the user's language. */
export const KIND_I18N: Record<string, string> = {
  "mfg→wholesaler": "link.kind.mfgToWholesaler",
  "wholesaler→mfg": "link.kind.wholesalerToMfg",
  "mfg→shop": "link.kind.mfgToShop",
  "shop→mfg": "link.kind.shopToMfg",
  "wholesaler→shop": "link.kind.wholesalerToShop",
  "shop→wholesaler": "link.kind.shopToWholesaler",
  "mfg→mfg": "link.kind.mfgToMfg",
  "wholesaler→wholesaler": "link.kind.wholesalerToWholesaler",
  "shop→shop": "link.kind.shopToShop",
};

/** Which end of a relationship we are on.
 *  `supplier` — the other company sells to us (they are our supplier).
 *  `client`   — we sell to the other company (they are our client). */
export type LinkDirection = "supplier" | "client";

/** Position in the supply chain; lower is further upstream, i.e. the seller.
 *  Goods flow manufacturer → wholesaler → shop. Mirrors `SUPPLY_RANK` in
 *  `backend/apps/tenants/link_api.py` — keep the two in step. */
const SUPPLY_RANK: Record<string, number> = { manufacturer: 0, wholesaler: 1, shop: 2 };

/** A kind reads "<initiator type>→<target type>". */
const KIND_SIDES: Record<string, [string, string]> = {
  "mfg→wholesaler": ["manufacturer", "wholesaler"],
  "wholesaler→mfg": ["wholesaler", "manufacturer"],
  "mfg→shop": ["manufacturer", "shop"],
  "shop→mfg": ["shop", "manufacturer"],
  "wholesaler→shop": ["wholesaler", "shop"],
  "shop→wholesaler": ["shop", "wholesaler"],
  "mfg→mfg": ["manufacturer", "manufacturer"],
  "wholesaler→wholesaler": ["wholesaler", "wholesaler"],
  "shop→shop": ["shop", "shop"],
};

/** Read a relationship kind from our own side: does it bring us a supplier or
 *  a client? Whoever sits further upstream is the seller.
 *
 *  For a symmetric kind this can't be decided without knowing who asked — use
 *  {@link directionForRequest} when you have the request itself. */
export function directionForKind(kind: string, myType: string): LinkDirection {
  const sides = KIND_SIDES[kind];
  const myRank = SUPPLY_RANK[myType];
  if (!sides || myRank === undefined) return "supplier";
  const otherType = sides[0] === myType ? sides[1] : sides[0];
  const otherRank = SUPPLY_RANK[otherType] ?? myRank;
  if (myRank === otherRank) return "supplier"; // we asked → we are the buyer
  return myRank > otherRank ? "supplier" : "client";
}

/** Which end of a concrete request we are on.
 *
 *  Read from the request itself rather than from our company type: a kind
 *  names both sides ("<initiator>→<target>"), and knowing whether we are the
 *  initiator says which of the two is us. That is exact where
 *  {@link directionForKind} can only guess — a company that is registered as a
 *  wholesaler but was invited *as a manufacturer* used to be filed by its own
 *  type and land on the wrong page, and a company holding several types has no
 *  single type to file by at all.
 *
 *  Ties (both sides at the same point in the chain) keep the initiator as the
 *  buyer, matching `_resolve_buyer_seller` on the backend so the two ends of
 *  the wire agree on who sells to whom. */
export function directionForRequest(
  req: Pick<LinkRequest, "kind" | "initiator_slug"> & {
    initiator_is_seller?: boolean | null;
  },
  mySlug: string,
  myType: string,
): LinkDirection {
  const iAmSender = req.initiator_slug === mySlug;
  // Composed on a page that knew the answer — "invite a client" or "invite a
  // supplier". Believe it over any inference from company type: a wholesaler
  // supplying a factory and one buying from it are the same pair of types.
  if (req.initiator_is_seller != null) {
    const iSell = iAmSender ? req.initiator_is_seller : !req.initiator_is_seller;
    return iSell ? "client" : "supplier";
  }
  const sides = KIND_SIDES[req.kind];
  if (!sides) return directionForKind(req.kind, myType);
  const iAmInitiator = req.initiator_slug === mySlug;
  const myRank = SUPPLY_RANK[iAmInitiator ? sides[0] : sides[1]];
  const otherRank = SUPPLY_RANK[iAmInitiator ? sides[1] : sides[0]];
  if (myRank === undefined || otherRank === undefined) {
    return directionForKind(req.kind, myType);
  }
  if (myRank === otherRank) return iAmInitiator ? "supplier" : "client";
  return myRank > otherRank ? "supplier" : "client";
}

/** What the *other* company on a request is — manufacturer, wholesaler or shop.
 *
 *  Read off the kind ("<initiator type>→<target type>") and which side we are
 *  on, so it holds for a company invited as one of several types it carries.
 *  Null when the kind is unknown. */
export function counterpartyTypeForRequest(
  req: Pick<LinkRequest, "kind" | "initiator_slug">,
  mySlug: string,
): string | null {
  const sides = KIND_SIDES[req.kind];
  if (!sides) return null;
  return req.initiator_slug === mySlug ? sides[1] : sides[0];
}

/** The kinds the compose modal offers, one per counterparty role.
 *
 *  Both directions offer all three: who we sell to and who supplies us is
 *  decided by the page ("invite a client" / "invite a supplier") and sent as
 *  `initiator_is_seller`, not guessed from company type. It used to be
 *  filtered by supply-chain rank, which left a wholesaler with a single option
 *  on the client side — a wholesaler that sells to another wholesaler, or
 *  supplies a factory, had no way to say so.
 *
 *  `direction` is kept in the signature: the caller passes it, and it stays the
 *  place to narrow the list again if a role turns out to be nonsense for a side.
 */
export function kindsForDirection(
  myTypes: string | string[],
  _direction: LinkDirection,
): string[] {
  const types = typeof myTypes === "string" ? [myTypes] : myTypes;
  return kindsForType(types);
}

/** The counterparty's role in a kind we are composing (we are the initiator).
 *  The compose dropdown labels its options with this rather than with
 *  "Wholesaler → Shop": on a page titled "Invite a client", the only new
 *  information is what kind of company the client is. */
export function counterpartyTypeForKind(kind: string, myType: string): string {
  const sides = KIND_SIDES[kind];
  if (!sides) return myType;
  return sides[0] === myType ? sides[1] : sides[0];
}

/** Every relationship we may open, given the position(s) we occupy. A company
 *  that both produces and distributes gets the union of the two lists — it can
 *  invite a shop as a manufacturer *and* source from a manufacturer as a
 *  wholesaler. */
export function kindsForType(myTypes: string | string[]): string[] {
  const types = typeof myTypes === "string" ? [myTypes] : myTypes;
  // One kind per counterparty role, in supply-chain order: producer, then
  // distributor, then shop.
  const byType: Record<string, string[]> = {
    manufacturer: ["mfg→mfg", "mfg→wholesaler", "mfg→shop"],
    wholesaler: ["wholesaler→mfg", "wholesaler→wholesaler", "wholesaler→shop"],
    shop: ["shop→mfg", "shop→wholesaler", "shop→shop"],
  };
  return [...new Set(types.flatMap((t) => byType[t] ?? []))];
}

export const searchTenant = (
  country: string,
  registrationNumber: string,
): Promise<TenantSearchResult> =>
  api.get<TenantSearchResult>(
    `/tenants/search?country=${encodeURIComponent(country)}&registration_number=${encodeURIComponent(registrationNumber)}`,
  );

/** What the partner on a request is *to us* — the unified form's status. */
export type PartnerRole = "client" | "supplier" | "both";

/** Read the unified-form status off a concrete request, from our side of it.
 *  Falls back to {@link directionForRequest} for pre-unification requests
 *  (whose direction was implied by the page they were composed on). Note the
 *  flip: `directionForRequest` answers "which of MY lists does this belong
 *  to", which is the same thing as what the counterparty is to me. */
export function roleForRequest(
  req: Pick<LinkRequest, "kind" | "initiator_slug"> & {
    initiator_is_seller?: boolean | null;
    both_directions?: boolean;
  },
  mySlug: string,
  myType: string,
): PartnerRole {
  if (req.both_directions) return "both";
  return directionForRequest(req, mySlug, myType);
}

export const createLinkRequest = (
  slug: string,
  payload: {
    /** Optional since the unified form: the backend files an empty kind under
     *  the initiator's same-type pairing. */
    kind?: string;
    /** true when we are the seller (they are our client). */
    initiator_is_seller?: boolean;
    /** "Оба" — client and supplier at once. */
    both_directions?: boolean;
    message?: string;
    contact_name?: string;
    language?: string;
    target_slug?: string;
    target_country?: string;
    target_registration_number?: string;
    target_email?: string;
    target_company_name?: string;
  },
): Promise<LinkRequest> =>
  api.post<LinkRequest>(`/t/${slug}/link-requests`, payload);

export const fetchPartnerMapPoints = (slug: string): Promise<PartnerMapPoint[]> =>
  api.get<PartnerMapPoint[]>(`/t/${slug}/partner-map-points`);

/** The per-partner "отобразить на карте" checkbox. */
export const setPartnerMapFlag = (
  slug: string,
  partnerSlug: string,
  showOnMap: boolean,
): Promise<{ show_on_map: boolean }> =>
  api.post(`/t/${slug}/partners/${partnerSlug}/map-flag`, { show_on_map: showOnMap });

export const fetchInbox = (slug: string, status?: string): Promise<LinkRequest[]> =>
  api.get<LinkRequest[]>(
    `/t/${slug}/link-requests/inbox${status ? `?status=${status}` : ""}`,
  );

export const fetchOutbox = (slug: string, status?: string): Promise<LinkRequest[]> =>
  api.get<LinkRequest[]>(
    `/t/${slug}/link-requests/outbox${status ? `?status=${status}` : ""}`,
  );

export const fetchInboxCount = (slug: string): Promise<{ pending: number }> =>
  api.get<{ pending: number }>(`/t/${slug}/link-requests/inbox/count`);

export const acceptLinkRequest = (slug: string, id: number): Promise<{ status: string; supplier_link_id: number }> =>
  api.post(`/t/${slug}/link-requests/${id}/accept`, {});

export const declineLinkRequest = (slug: string, id: number): Promise<{ status: string }> =>
  api.post(`/t/${slug}/link-requests/${id}/decline`, {});

/** Withdraw a request we sent, or clear an answered one from our list. Never
 *  touches an accepted link; unlinking is separate. */
export const deleteLinkRequest = (slug: string, id: number): Promise<void> =>
  api.delete(`/t/${slug}/link-requests/${id}`);

/** Clear an unanswered incoming request, with a note back to the sender. Their
 *  copy changes status rather than disappearing, so "no answer yet" and "they
 *  are not interested" stop looking the same. */
export const removeLinkRequest = (
  slug: string,
  id: number,
  comment: string,
): Promise<LinkRequest> =>
  api.post<LinkRequest>(`/t/${slug}/link-requests/${id}/remove`, { comment });

/** Correct an invitation that has not been answered yet. Replaces re-sending
 *  it: the recipient already has it, so what changes is what it says. Its
 *  place in their list is unaffected. */
export const editLinkRequest = (
  slug: string,
  id: number,
  data: {
    message?: string;
    contact_name?: string;
    target_email?: string;
    language?: string;
  },
): Promise<LinkRequest> =>
  api.patch<LinkRequest>(`/t/${slug}/link-requests/${id}`, data);

/** Ask again after being removed — allowed 24h on, and only then. */
export const resendLinkRequest = (slug: string, id: number): Promise<LinkRequest> =>
  api.post<LinkRequest>(`/t/${slug}/link-requests/${id}/resend`, {});

export const fetchVisibility = (slug: string, linkId: number): Promise<LinkVisibility> =>
  api.get<LinkVisibility>(`/t/${slug}/supplier-links/${linkId}/visibility`);

export const updateVisibility = (
  slug: string,
  linkId: number,
  data: Partial<Omit<LinkVisibility, "supplier_link_id">>,
): Promise<LinkVisibility> =>
  api.patch<LinkVisibility>(`/t/${slug}/supplier-links/${linkId}/visibility`, data);

export const acceptLinkTou = (): Promise<void> =>
  api.post("/me/accept-link-tou", {});

export type CatalogNode = {
  id: number;
  name: string;
  parent_id: number | null;
  type: "category" | "product";
};

export const fetchCatalogTree = (slug: string): Promise<CatalogNode[]> =>
  api.get<CatalogNode[]>(`/t/${slug}/catalog/tree`);

/* ── Partners recorded by hand (2026-09-11) ─────────────────────────────
   A client that has to be invoiced today but is not on the platform: the
   form records its requisites and the server keeps them as a company row
   of its own, linked to us. An invitation may go with it; whoever signs up
   through that invitation takes the profile over. */

export type PartnerRequisitesForm = {
  company_name: string;
  registration_number: string;
  vat_number: string;
  country: string;
  legal_address: string;
  physical_address: string;
  bank_name: string;
  bank_iban: string;
  bank_swift: string;
  licences: string;
  contact_email: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_position: string;
  contact_phone: string;
  /** Our own note on the partner; not handed over when they claim the profile. */
  contact_comment: string;
  accountant_first_name: string;
  accountant_last_name: string;
  accountant_position: string;
  accountant_phone: string;
  accountant_email: string;
};

export const blankPartnerRequisites = (country = ""): PartnerRequisitesForm => ({
  company_name: "",
  registration_number: "",
  vat_number: "",
  country,
  legal_address: "",
  physical_address: "",
  bank_name: "",
  bank_iban: "",
  bank_swift: "",
  licences: "",
  contact_email: "",
  contact_first_name: "",
  contact_last_name: "",
  contact_position: "",
  contact_phone: "",
  contact_comment: "",
  accountant_first_name: "",
  accountant_last_name: "",
  accountant_position: "",
  accountant_phone: "",
  accountant_email: "",
});

export type OfflinePartnerResult = {
  slug: string;
  display_name: string;
  supplier_link_ids: number[];
  link_request_id: number | null;
  /** The company was on the platform already: an ordinary partner request
   *  went to its inbox and nothing was recorded by hand. */
  registered: boolean;
};

export const createOfflinePartner = (
  slug: string,
  payload: PartnerRequisitesForm & {
    role: PartnerRole;
    send_invite: boolean;
    message?: string;
    language?: string;
  },
) => api.post<OfflinePartnerResult>(`/t/${slug}/partners/offline`, payload);

export const updatePlaceholderPartner = (
  slug: string,
  partnerSlug: string,
  payload: PartnerRequisitesForm,
) => api.patch<unknown>(`/t/${slug}/partners/${partnerSlug}/placeholder`, payload);

/** Invite a partner recorded without an invitation, after the fact. Files
 *  the same pending request the add-partner form would have, against the
 *  same placeholder, and mails the same invitation. */
export const inviteOfflinePartner = (
  slug: string,
  partnerSlug: string,
  payload: { contact_email: string; message?: string; language?: string },
) => api.post<LinkRequest>(`/t/${slug}/partners/offline/${partnerSlug}/invite`, payload);

/* ── Badge refresh ───────────────────────────────────────────────────────
   The sidebar «My partners» badge counts pending incoming requests. The
   Partners page knows that count exactly after every reload of its lists, so
   it announces it here rather than leaving the badge to the realtime channel
   (which is what left a stale «1» after an accept). */

export const LINK_REQUESTS_CHANGED_EVENT = "oe:link-requests-changed";

export function announcePendingInbox(pending: number): void {
  window.dispatchEvent(new CustomEvent(LINK_REQUESTS_CHANGED_EVENT, { detail: { pending } }));
}
